import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { HeartHandshake, Sprout, Scale, Lightbulb, Sparkles } from 'lucide-react';
import { setPageMeta } from '../utils/seo';

/**
 * CoreValues Page
 * Displays P.S. 155's five core values: Empathy, Collective Growth, Equity, Creativity, and Joy
 */
const CoreValues = () => {
  useEffect(() => {
    setPageMeta({
      title: 'Core Values — One of the Community Schools in Harlem | PS 155 William Paca',
      description: 'P.S. 155 William Paca is one of the community schools in Harlem built on empathy, equity, collective growth, creativity, and joy for every student.',
    });
  }, []);
  const values = [
    {
      title: 'Empathy',
      Icon: HeartHandshake,
      description: 'We lead with compassion and understanding—listening deeply, valuing every voice, and nurturing relationships that honor our shared humanity.',
      accentColor: 'text-rose-700',
      bgPattern: 'bg-rose-50',
      decorativeColor: 'bg-rose-100'
    },
    {
      title: 'Collective Growth',
      Icon: Sprout,
      description: 'We believe learning is a shared journey. Through reflection, collaboration, and courage, we grow together—as students, educators, and families—lifting one another toward excellence.',
      accentColor: 'text-emerald-700',
      bgPattern: 'bg-emerald-50',
      decorativeColor: 'bg-emerald-100'
    },
    {
      title: 'Equity',
      Icon: Scale,
      description: 'We are committed to fairness and inclusion, ensuring every child has access to the opportunities, resources, and high expectations needed to thrive.',
      accentColor: 'text-school-blue',
      bgPattern: 'bg-blue-50',
      decorativeColor: 'bg-blue-100'
    },
    {
      title: 'Creativity',
      Icon: Lightbulb,
      description: 'We inspire imagination and curiosity, encouraging our students to explore ideas, take risks, and express themselves authentically.',
      accentColor: 'text-purple-600',
      bgPattern: 'bg-purple-50',
      decorativeColor: 'bg-purple-100'
    },
    {
      title: 'Joy',
      Icon: Sparkles,
      description: 'We find joy in learning, in community, and in each child\'s growth—celebrating small victories and collective successes with East Harlem pride.',
      accentColor: 'text-school-gold-dark',
      bgPattern: 'bg-amber-50',
      decorativeColor: 'bg-amber-100'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-neutral-800 selection:bg-school-gold selection:text-white">
      <Navbar />

      <main id="main-content" tabIndex="-1" className="outline-none">
        {/* Hero Section */}
        <section className="relative pt-32 sm:pt-40 md:pt-48 pb-12 sm:pb-14 md:pb-16 overflow-hidden bg-school-blue">
          <div className="absolute inset-0 bg-school-blue-dark"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-block py-1 px-3 rounded-full bg-school-blue text-school-gold-on-blue text-xs sm:text-sm font-bold uppercase tracking-wider mb-3 sm:mb-4 border border-white/10">
              Our Foundation
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-tight">
              Core <span className="text-school-gold-on-blue">Values</span>
            </h1>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-school-gold mx-auto mb-4 sm:mb-6 rounded-full"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto font-light leading-relaxed px-4">
              At P.S. 155 William Paca, our community grows with passion, purpose, and East Harlem Pride. Rooted in love for our students and neighborhood, we are guided by these five values.
            </p>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="py-10 sm:py-11 md:py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16 sm:space-y-24 md:space-y-32">
              {values.map((value, index) => (
                <div
                  key={index}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-16 items-center`}
                >
                  {/* Icon Side - Creative Visual Element */}
                  <div className="flex-shrink-0 w-full lg:w-1/2 relative">
                    <div className="relative aspect-square max-w-md mx-auto">
                      {/* Decorative Background Elements */}
                      <div className={`absolute inset-0 ${value.bgPattern} rounded-full blur-3xl opacity-40 animate-pulse`}></div>

                      {/* Decorative Circles */}
                      <div className={`absolute top-0 right-0 w-32 h-32 ${value.decorativeColor} rounded-full opacity-20 -z-10`}></div>
                      <div className={`absolute bottom-0 left-0 w-40 h-40 ${value.decorativeColor} rounded-full opacity-20 -z-10`}></div>

                      {/* Main Icon Container */}
                      <div className={`relative z-10 w-full h-full ${value.bgPattern} rounded-3xl shadow-2xl flex items-center justify-center transform hover:scale-105 transition-transform duration-500 border-4 border-white`}>
                        <div className="relative">
                          {/* Icon Glow Effect */}
                          <div className={`absolute inset-0 ${value.accentColor} opacity-20 blur-2xl rounded-full`}></div>

                          {/* Icon */}
                          <div className={`relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 ${value.accentColor} p-6 sm:p-8`}>
                            <value.Icon className="w-full h-full" strokeWidth={1.5} />
                          </div>
                        </div>
                      </div>

                      {/* Decorative Dots Pattern */}
                      <div className="absolute -bottom-4 -right-4 grid grid-cols-3 gap-2 opacity-30">
                        {[...Array(9)].map((_, i) => (
                          <div key={i} className={`w-3 h-3 ${value.decorativeColor} rounded-full`}></div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className="flex-1 text-center lg:text-left">
                    <div className="inline-block mb-4">
                      <span className={`text-xs sm:text-sm font-bold ${value.accentColor} uppercase tracking-widest px-4 py-2 ${value.bgPattern} rounded-full`}>
                        Value #{index + 1}
                      </span>
                    </div>

                    <h2 className={`text-4xl sm:text-5xl md:text-6xl font-bold ${value.accentColor} mb-6 sm:mb-8 leading-tight`}>
                      {value.title}
                    </h2>

                    <div className={`w-24 h-1.5 ${value.decorativeColor} rounded-full mb-6 sm:mb-8 ${index % 2 === 0 ? 'lg:mr-auto' : 'lg:ml-auto'} mx-auto lg:mx-0`}></div>

                    <p className="text-lg sm:text-xl md:text-2xl text-neutral-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                      {value.description}
                    </p>

                    {/* Decorative Quote Mark */}
                    <div className={`mt-8 ${value.accentColor} opacity-10`}>
                      <svg className="w-16 h-16 sm:w-20 sm:h-20" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="relative py-20 sm:py-24 md:py-32 overflow-hidden">
          {/* Background with Pattern */}
          <div className="absolute inset-0 bg-school-blue-dark"></div>

          {/* Decorative Elements */}
          <div className="absolute top-10 left-10 w-72 h-72 bg-school-gold rounded-full opacity-5 blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400 rounded-full opacity-5 blur-3xl"></div>

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-school-blue rounded-full border border-white/20">
              <span className="text-school-gold-on-blue font-bold text-sm uppercase tracking-wider">Join Our Community</span>
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
              Growing Together with <span className="text-school-gold-on-blue">East Harlem Pride</span>
            </h2>

            <div className="w-24 h-1.5 bg-school-gold mx-auto rounded-full mb-8"></div>

            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-10 sm:mb-12 leading-relaxed max-w-3xl mx-auto">
              These values guide everything we do at P.S. 155, shaping how we teach, learn, and grow as a community.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <a
                href="/about"
                className="group px-8 py-4 bg-school-gold text-school-blue-dark rounded-full font-bold text-base sm:text-lg shadow-2xl hover:bg-yellow-500 hover:shadow-school-gold/50 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
              >
                Learn More About Us
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="/contact"
                className="group px-8 py-4 bg-white text-school-blue rounded-full font-bold text-base sm:text-lg shadow-2xl hover:bg-neutral-50 hover:shadow-white/50 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
              >
                Visit Our School
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CoreValues;
